import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateShift, type ShiftType } from "@/api/scheduling";
import { format } from "date-fns";
import { toast } from "sonner";

interface AssignShiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operatorId: string;
  operatorName: string;
  dispatchCenterId: string;
  date: Date;
  suggestedShift?: ShiftType;
}

const SHIFT_TYPES: {
  value: ShiftType;
  label: string;
  defaultStart: string;
  defaultEnd: string;
}[] = [
  {
    value: "MORNING",
    label: "Morning (6:00-12:00)",
    defaultStart: "06:00",
    defaultEnd: "12:00",
  },
  {
    value: "AFTERNOON",
    label: "Afternoon (12:00-18:00)",
    defaultStart: "12:00",
    defaultEnd: "18:00",
  },
  {
    value: "EVENING",
    label: "Evening (18:00-24:00)",
    defaultStart: "18:00",
    defaultEnd: "00:00",
  },
];

export function AssignShiftDialog({
  open,
  onOpenChange,
  operatorId,
  operatorName,
  dispatchCenterId,
  date,
  suggestedShift,
}: AssignShiftDialogProps) {
  const defaultShift = suggestedShift ?? "MORNING";
  const [shiftType, setShiftType] = useState<ShiftType>(defaultShift);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [notes, setNotes] = useState("");

  // Set default times when suggestedShift changes or dialog opens
  useEffect(() => {
    if (open && suggestedShift) {
      const config = SHIFT_TYPES.find((t) => t.value === suggestedShift);
      if (config) {
        setShiftType(suggestedShift);
        setStartTime(config.defaultStart);
        setEndTime(config.defaultEnd);
      }
    }
  }, [open, suggestedShift]);

  const createShift = useCreateShift({
    onSuccess: () => {
      toast.success("Shift assigned");
      onOpenChange(false);
      resetForm();
    },
    onError: (error) => {
      toast.error("Failed to assign shift", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });

  const resetForm = () => {
    setShiftType("MORNING");
    setStartTime("");
    setEndTime("");
    setNotes("");
  };

  const handleShiftTypeChange = (value: ShiftType) => {
    setShiftType(value);
    const config = SHIFT_TYPES.find((t) => t.value === value);
    if (config) {
      setStartTime(config.defaultStart);
      setEndTime(config.defaultEnd);
    }
  };

  const handleSubmit = () => {
    createShift.mutate({
      operatorId,
      dispatchCenterId,
      date: format(date, "yyyy-MM-dd"),
      shiftType,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Shift</DialogTitle>
          <DialogDescription>
            Assign a shift to {operatorName} on {format(date, "EEEE, MMMM d")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Shift Type</Label>
            <Select value={shiftType} onValueChange={handleShiftTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHIFT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                placeholder="HH:MM"
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                placeholder="HH:MM"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createShift.isPending}>
            {createShift.isPending ? "Assigning..." : "Assign Shift"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
